import * as Sentry from "@sentry/react";
import { useEffect } from 'react'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

// Build tracePropagationTargets from environment
const tracePropagationTargets = [/^\//];
if (import.meta.env.VITE_APP_API_URL) {
  tracePropagationTargets.push(new RegExp(`^${import.meta.env.VITE_APP_API_URL}`));
}

Sentry.init({
  dsn: "https://c5993958b4b44b31d582e43a186fd101@o4510725982322688.ingest.de.sentry.io/4510726027739216",

  // Adds request headers and IP for users
  sendDefaultPii: true,

  // Enable logs to be sent to Sentry
  enableLogs: true,

  // Intercept all events before sending to Sentry to store locally
  beforeSend(event, hint) {
    // Store all Sentry events locally for admin dashboard
    if (window.addSentryEvent) {
      const eventData = {
        id: event.event_id || `event-${Date.now()}`,
        title: event.message || event.exception?.values?.[0]?.value || 'Sentry Event',
        message: event.message || event.exception?.values?.[0]?.value || 'Unknown event',
        category: event.exception ? 'error' : 'custom_message',
        level: event.level || 'info',
        timestamp: event.timestamp ? new Date(event.timestamp * 1000).toISOString() : new Date().toISOString(),
        user: getCurrentUser(),
        context: {
          captured_via: 'beforeSend',
          ...event.contexts,
          ...event.extra
        },
        count: 1,
        firstSeen: event.timestamp ? new Date(event.timestamp * 1000).toISOString() : new Date().toISOString(),
        lastSeen: event.timestamp ? new Date(event.timestamp * 1000).toISOString() : new Date().toISOString()
      };
      
      window.addSentryEvent(eventData);
    }
    
    // Return event to continue sending to Sentry
    return event;
  },

  integrations: [
    // Browser tracing for performance monitoring
    Sentry.browserTracingIntegration(),

    // React Router v7 integration for navigation tracing
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),

    // Session Replay integration
    Sentry.replayIntegration(),

    // User Feedback integration
    Sentry.feedbackIntegration({
      colorScheme: "system",
    }),
  ],

  // Capture 100% of transactions for tracing
  tracesSampleRate: 1.0,

  // Control which URLs trace propagation should be enabled for
  tracePropagationTargets,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Export logger for use throughout the app
export const { logger } = Sentry;

// Store for captured events (for admin dashboard)
window.sentryEventStore = window.sentryEventStore || [];

// Load existing data from localStorage on startup
try {
  const storedEvents = localStorage.getItem('sentryEventStore');
  if (storedEvents) {
    window.sentryEventStore = JSON.parse(storedEvents);
    console.log('Loaded existing events from localStorage:', window.sentryEventStore.length);
  }
} catch (e) {
  console.error('Error loading events from localStorage:', e);
  window.sentryEventStore = [];
}

// Test function to verify storage is working
window.testSentryStorage = function() {
  const testEvent = {
    id: `test-${Date.now()}`,
    title: 'Test Event',
    message: 'Testing local storage functionality',
    category: 'custom_message',
    level: 'info',
    timestamp: new Date().toISOString(),
    user: {
      id: 'test-user',
      email: 'test@example.com',
      username: 'test'
    },
    context: { test: true },
    count: 1,
    firstSeen: new Date().toISOString(),
    lastSeen: new Date().toISOString()
  };
  
  window.sentryEventStore.unshift(testEvent);
  
  // Save to localStorage
  try {
    localStorage.setItem('sentryEventStore', JSON.stringify(window.sentryEventStore));
    console.log('Test event saved to localStorage');
  } catch (e) {
    console.error('Error saving to localStorage:', e);
  }
  
  window.dispatchEvent(new CustomEvent('sentryEventAdded', { detail: testEvent }));
  console.log('Test event added:', testEvent);
  console.log('Current store:', window.sentryEventStore);
  return testEvent;
};

// Helper function to get current user
function getCurrentUser() {
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      return {
        id: user?._id || 'unknown',
        email: user?.email,
        username: user?.name,
        role: user?.role
      };
    }
  } catch (e) {
    console.error('Error getting user data:', e);
  }
  
  return {
    id: 'anonymous',
    email: 'anonymous@example.com',
    username: 'anonymous',
    role: 'anonymous'
  };
}

// Simple function to add events to local storage for dashboard
window.addSentryEvent = function(eventData) {
  console.log('addSentryEvent called with:', eventData);
  
  if (!window.sentryEventStore) {
    window.sentryEventStore = [];
    console.log('Initialized sentryEventStore');
  }
  
  // Check if similar event exists (same title and user)
  const existingEventIndex = window.sentryEventStore.findIndex(
    event => event.title === eventData.title && event.user.email === eventData.user.email
  );
  
  if (existingEventIndex !== -1) {
    // Update existing event
    window.sentryEventStore[existingEventIndex].count += 1;
    window.sentryEventStore[existingEventIndex].lastSeen = eventData.timestamp;
    window.sentryEventStore[existingEventIndex].timestamp = eventData.timestamp;
    console.log('Updated existing event at index:', existingEventIndex);
  } else {
    // Add new event
    window.sentryEventStore.unshift(eventData);
    console.log('Added new event. Total events:', window.sentryEventStore.length);
  }
  
  // Keep only last 50 events
  if (window.sentryEventStore.length > 50) {
    window.sentryEventStore = window.sentryEventStore.slice(0, 50);
  }
  
  // Save to localStorage
  try {
    localStorage.setItem('sentryEventStore', JSON.stringify(window.sentryEventStore));
    console.log('✅ Events saved to localStorage. Total:', window.sentryEventStore.length);
  } catch (e) {
    console.error('❌ Error saving to localStorage:', e);
  }
  
  // Trigger custom event for real-time updates
  window.dispatchEvent(new CustomEvent('sentryEventAdded', { detail: eventData }));
  console.log('Dispatched sentryEventAdded event');
  
  // Also log current state
  console.log('Current sentryEventStore:', window.sentryEventStore);
};

// Simple functions to manually add events when needed
window.captureManualError = function(error, context = {}) {
  // Send to Sentry
  Sentry.captureException(error);
  
  // Add to local dashboard
  const eventData = {
    id: `manual-${Date.now()}`,
    title: `Manual Error: ${error.message}`,
    message: error.message,
    category: 'manual_error',
    level: 'error',
    timestamp: new Date().toISOString(),
    user: getCurrentUser(),
    context: {
      ...context,
      stack: error.stack,
      manual_capture: true
    },
    count: 1,
    firstSeen: new Date().toISOString(),
    lastSeen: new Date().toISOString()
  };
  
  window.addSentryEvent(eventData);
};

window.captureCustomMessage = function(message, level = 'info', context = {}) {
  // Send to Sentry
  Sentry.captureMessage(message);
  
  // Add to local dashboard
  const eventData = {
    id: `custom-${Date.now()}`,
    title: `Custom Message: ${message}`,
    message: message,
    category: 'custom_message',
    level: level,
    timestamp: new Date().toISOString(),
    user: getCurrentUser(),
    context: {
      ...context,
      custom_message: true
    },
    count: 1,
    firstSeen: new Date().toISOString(),
    lastSeen: new Date().toISOString()
  };
  
  window.addSentryEvent(eventData);
};

window.captureApiError = function(error, apiEndpoint, requestData = {}) {
  // Send to Sentry
  Sentry.captureException(error);
  
  // Add to local dashboard
  const eventData = {
    id: `api-${Date.now()}`,
    title: `API Error: ${error.message}`,
    message: error.message,
    category: 'api_error',
    level: 'error',
    timestamp: new Date().toISOString(),
    user: getCurrentUser(),
    context: {
      api_endpoint: apiEndpoint,
      method: requestData.method || 'unknown',
      status_code: requestData.status_code,
      error_message: error.message,
      response_data: requestData.response_data
    },
    count: 1,
    firstSeen: new Date().toISOString(),
    lastSeen: new Date().toISOString()
  };
  
  window.addSentryEvent(eventData);
};
// Wrapper functions that call Sentry and also store locally
// These can be used instead of directly calling Sentry.captureException/captureMessage
export const captureException = function(error, captureContext) {
  // Call original Sentry function
  const result = Sentry.captureException(error, captureContext);
  
  // Store locally for admin dashboard
  const eventData = {
    id: `error-${Date.now()}`,
    title: `Error: ${error?.message || 'Unknown error'}`,
    message: error?.message || 'Unknown error',
    category: 'manual_error',
    level: 'error',
    timestamp: new Date().toISOString(),
    user: getCurrentUser(),
    context: {
      stack: error?.stack,
      name: error?.name,
      captured_via: 'captureException',
      ...captureContext
    },
    count: 1,
    firstSeen: new Date().toISOString(),
    lastSeen: new Date().toISOString()
  };
  
  if (window.addSentryEvent) {
    window.addSentryEvent(eventData);
  }
  
  return result;
};

export const captureMessage = function(message, captureContext) {
  // Call original Sentry function
  const result = Sentry.captureMessage(message, captureContext);
  
  // Store locally for admin dashboard
  const eventData = {
    id: `message-${Date.now()}`,
    title: `Message: ${message}`,
    message: message,
    category: 'custom_message',
    level: captureContext?.level || 'info',
    timestamp: new Date().toISOString(),
    user: getCurrentUser(),
    context: {
      captured_via: 'captureMessage',
      ...captureContext
    },
    count: 1,
    firstSeen: new Date().toISOString(),
    lastSeen: new Date().toISOString()
  };
  
  if (window.addSentryEvent) {
    window.addSentryEvent(eventData);
  }
  
  return result;
};
